import React, { useEffect, useState } from 'react';
import { bookingApi } from '../../utils/bookingApi';
import CoachPicker, { coachNames } from './CoachPicker';
import { Sheet, useToast } from '../ui';
import './CoachPicker.css';
import { typeName } from '../../utils/sessionTypes';

const sameIds = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);

// One session's coaches on its register, with changing them for this date
// only: cover before it runs, or who actually coached once it has ended
// (that's what coach hours count).
function SessionCoaches({ row, label, staff, onChanged, canChangePast }) {
  const toast = useToast();
  const [editing, setEditing] = useState(null); // ids being picked, or null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const startIds = row.coaches.map(c => c.id);

  const save = async (send, done) => {
    setSaving(true);
    setError(null);
    try {
      await send();
      setEditing(null);
      await onChanged();
      toast(done);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't save the coaches.");
    } finally {
      setSaving(false);
    }
  };

  const note = row.ownList ? (row.ended ? 'who coached' : 'for this date') : null;
  const title = `${label ? `${label}: ` : ''}${row.ended ? 'Who coached this session' : 'Coaches for this date'}`;
  const canChange = !row.ended || canChangePast;
  const canReset = row.ownList && !row.ended;
  return (
    <div className="register-coaches__row">
      <span>
        {label && <strong>{label}: </strong>}
        {row.coaches.length ? coachNames(row.coaches) : 'No coaches'}
        {note && <span className="register-coaches__note"> ({note})</span>}
      </span>
      {(canChange || canReset) && (
        <div className="bk-row-actions">
          {canChange && (
            <button type="button" className="bk-btn bk-btn--sm" disabled={!staff}
              onClick={() => { setError(null); setEditing(startIds); }}>
              {row.ended ? 'Change who coached' : 'Change coaches'}
            </button>
          )}
          {canReset && (
            <button type="button" className="bk-btn bk-btn--sm" disabled={saving}
              onClick={() => save(() => bookingApi.clearDateCoaches(row.instanceId), 'Back to the usual coaches')}>
              Use usual coaches
            </button>
          )}
        </div>
      )}
      {error && !editing && <p className="bk-error" role="alert" style={{ margin: '0.4rem 0 0' }}>{error}</p>}
      {editing && (
        <Sheet
          title={title}
          onClose={() => setEditing(null)}
          dirty={!sameIds(editing, startIds)}
          onSubmit={() => save(() => bookingApi.setDateCoaches(row.instanceId, editing), 'Coaches saved')}
          footer={(requestClose) => (
            <div className="bk-actions">
              <button type="submit" className="bk-btn bk-btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button type="button" className="bk-btn" disabled={saving} onClick={requestClose}>Cancel</button>
            </div>
          )}
        >
          <CoachPicker staff={staff || []} known={row.coaches} value={editing} onChange={setEditing} label="Coaches" />
          {error && <p className="bk-form-error" role="alert">{error}</p>}
        </Sheet>
      )}
    </div>
  );
}

// The coaches block at the top of a register. coaches: one row per session
// in the slot (from the attendance route). canChangePast: staff with session setup, who
// can change who coached once a session has ended (it feeds coach hours).
export default function RegisterCoaches({ coaches, onChanged, canChangePast = false }) {
  const [staff, setStaff] = useState(null);
  useEffect(() => {
    bookingApi.getCoachStaff().then(r => setStaff(r.data)).catch(() => setStaff([]));
  }, []);
  const several = coaches.length > 1;
  return (
    <section className="register-coaches" aria-labelledby="register-coaches-title">
      <h2 className="register-coaches__title" id="register-coaches-title">Coaches</h2>
      {coaches.map(row => (
        <SessionCoaches key={row.instanceId} row={row} staff={staff} onChanged={onChanged} canChangePast={canChangePast}
          label={several ? typeName(row) : null} />
      ))}
    </section>
  );
}
